<template>
  <div class="space-y-2">
    <div class="flex items-center justify-between gap-2">
      <div class="text-xs text-gray-500 dark:text-gray-400">
        <span v-if="props.mapping?.modbusDataFormat"
          >{{ tt('Expected format: ', 'Pričakovana oblika: ') }}{{ props.mapping.modbusDataFormat }}</span
        >
        <span v-else>{{ tt('Select a Modbus register', 'Izberite register Modbus') }}</span>
        <span v-if="props.mapping?.modbusTableType"> • {{ props.mapping.modbusTableType }}</span>
      </div>
      <button
        v-if="localConfig.propertyId"
        type="button"
        class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        @click="clearSelection">
        {{ tt('Clear', 'Počisti') }}
      </button>
    </div>

    <select
      v-model="selectedPropertyId"
      class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      :disabled="loading"
      @change="onSelectionChange">
      <option value="">{{ tt('Select register...', 'Izberite register...') }}</option>
      <option
        v-for="register in filteredRegisters"
        :key="register.modbus_register_id"
        :value="register.modbus_register_id">
        {{ formatRegisterLabel(register) }}
      </option>
    </select>

    <div v-if="error" class="text-xs text-red-600 dark:text-red-400">
      {{ error }}
    </div>
    <div v-else-if="loading" class="text-xs text-gray-500 dark:text-gray-400">
      {{ tt('Loading Modbus registers...', 'Nalaganje registrov Modbus...') }}
    </div>
    <div v-else-if="!filteredRegisters.length" class="text-xs text-gray-500 dark:text-gray-400 italic">
      {{ tt('No compatible Modbus registers available.', 'Ni na voljo združljivih registrov Modbus.') }}
    </div>

    <div v-if="localConfig.propertyId" class="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
      <div>
        <span class="font-medium">{{ tt('Address:', 'Naslov:') }}</span>
        {{ localConfig.slaveAddress }}:{{ localConfig.registerAddress }}
      </div>
      <div>
        <span class="font-medium">{{ tt('Table:', 'Tabela:') }}</span>
        {{ localConfig.tableType }}
      </div>
      <div>
        <span class="font-medium">{{ tt('Format:', 'Oblika:') }}</span>
        {{ localConfig.dataFormat }}
      </div>
      <div v-if="localConfig.byteOrder">
        <span class="font-medium">{{ tt('Byte order:', 'Vrstni red bajtov:') }}</span>
        {{ localConfig.byteOrder }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { PropertyMapping } from '../shared/propertyMapping';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

interface ModbusRegisterOption {
  modbus_register_id: string;
  slave_address: number;
  table_type: string;
  register_address: number;
  register_name?: string;
  data_format: string;
  byte_order?: string | null;
}

export interface ModbusConfig {
  propertyId: string;
  slaveAddress: number;
  tableType: string;
  registerAddress: number;
  dataFormat: string;
  byteOrder?: string | null;
  registerName?: string;
}

interface Props {
  modelValue?: ModbusConfig;
  mapping: PropertyMapping | undefined;
  integrationId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: ModbusConfig];
}>();

const loading = ref(false);
const error = ref<string | null>(null);
const registers = ref<ModbusRegisterOption[]>([]);

const localConfig = ref<ModbusConfig>({
  propertyId: props.modelValue?.propertyId || '',
  slaveAddress: props.modelValue?.slaveAddress ?? 0,
  tableType: props.modelValue?.tableType || props.mapping?.modbusTableType || '',
  registerAddress: props.modelValue?.registerAddress ?? 0,
  dataFormat: props.modelValue?.dataFormat || props.mapping?.modbusDataFormat || '',
  byteOrder: props.modelValue?.byteOrder ?? null,
  registerName: props.modelValue?.registerName,
});

const selectedPropertyId = ref(localConfig.value.propertyId);

const filteredRegisters = computed(() => {
  return registers.value.filter((register) => {
    if (props.mapping?.modbusTableType && register.table_type !== props.mapping.modbusTableType) {
      return false;
    }

    if (props.mapping?.modbusDataFormat && register.data_format !== props.mapping.modbusDataFormat) {
      return false;
    }

    return true;
  });
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      return;
    }

    localConfig.value = { ...newValue };
    selectedPropertyId.value = newValue.propertyId || '';
  },
  { deep: true },
);

watch(
  () => props.integrationId,
  async () => {
    await loadRegisters();
  },
);

onMounted(async () => {
  await loadRegisters();
});

function formatRegisterLabel(register: ModbusRegisterOption): string {
  const name = register.register_name ? ` - ${register.register_name}` : '';
  return `${register.slave_address}:${register.table_type}:${register.register_address}${name} (${register.data_format})`;
}

function clearSelection() {
  localConfig.value = {
    propertyId: '',
    slaveAddress: 0,
    tableType: props.mapping?.modbusTableType || '',
    registerAddress: 0,
    dataFormat: props.mapping?.modbusDataFormat || '',
    byteOrder: null,
    registerName: undefined,
  };
  selectedPropertyId.value = '';
  emit('update:modelValue', { ...localConfig.value });
}

function onSelectionChange() {
  const register = registers.value.find((item) => item.modbus_register_id === selectedPropertyId.value);

  if (!register) {
    clearSelection();
    return;
  }

  localConfig.value = {
    propertyId: register.modbus_register_id,
    slaveAddress: register.slave_address,
    tableType: register.table_type,
    registerAddress: register.register_address,
    dataFormat: register.data_format,
    byteOrder: register.byte_order ?? null,
    registerName: register.register_name,
  };

  emit('update:modelValue', { ...localConfig.value });
}

async function loadRegisters() {
  if (!props.integrationId) {
    registers.value = [];
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    const data = await apiFetch(`/integrations/${props.integrationId}/datapoints`);
    registers.value = data.modbus || [];
  } catch (err) {
    console.error('[ModbusReferenceInput] Failed to load registers', err);
    error.value =
      err instanceof Error
        ? err.message
        : tt('Failed to load Modbus registers', 'Registrov Modbus ni bilo mogoče naložiti');
  } finally {
    loading.value = false;
  }
}
</script>
